import { useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { JobSearchQueryParams } from '@type/filters';

type Params = {
  queryParams: Partial<JobSearchQueryParams> | undefined;
  setQueryParams: (updater: (prev: JobSearchQueryParams | undefined) => JobSearchQueryParams) => void;
  setCurrentPage: Dispatch<SetStateAction<string | number>>;
  // SWR Infinite setter to force revalidation
  setSize: (size: number | ((curr: number) => number)) => Promise<unknown> | void;
};

/**
 * useJobListLocationRestore
 * - Captures initial SSR coords and persists them once.
 * - Exposes a method to remember last geolocated coords ("Use my location").
 * - Restores coords when manual location is cleared.
 * - Forces revalidation when coords become available and no manual location is set.
 */
export const useJobListLocationRestore = ({
  queryParams,
  setQueryParams,
  setCurrentPage,
  setSize,
}: Params) => {
  // Capture initial SSR coords (and persist once)
  const initialCoordsRef = useRef<{ lat: string; lon: string } | null>(null);
  useEffect(() => {
    const latStr = queryParams?._lat;
    const lonStr = queryParams?._lon;
    if (latStr && lonStr && !initialCoordsRef.current) {
      initialCoordsRef.current = { lat: String(latStr), lon: String(lonStr) };
      try {
        localStorage.setItem('joblist.initialCoords', JSON.stringify(initialCoordsRef.current));
      } catch {
        // ignore storage errors
      }
    }
  }, [queryParams?._lat, queryParams?._lon]);

  // Helper to safely read coords from localStorage
  const parseStoredCoords = (key: string): { lat: string; lon: string } | null => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const obj: unknown = JSON.parse(raw);
      if (obj && typeof obj === 'object' && 'lat' in obj && 'lon' in obj) {
        const { lat, lon } = obj;
        if (typeof lat === 'string' && typeof lon === 'string') return { lat, lon };
      }
      return null;
    } catch {
      return null;
    }
  };

  // When manual location is cleared and no coords exist, restore saved coords and refilter
  useEffect(() => {
    const locArr = queryParams?.location ?? undefined;
    const hasManual = Array.isArray(locArr) && locArr.length > 0;
    const hasCoords = Boolean(queryParams?._lat && queryParams?._lon);

    if (!hasManual && !hasCoords) {
      let fallback: { lat: string; lon: string } | null = initialCoordsRef.current;
      if (!fallback) {
        fallback = parseStoredCoords('joblist.initialCoords');
      }
      if (!fallback) {
        fallback = parseStoredCoords('joblist.lastCoords');
      }

      if (fallback?.lat && fallback?.lon) {
        setCurrentPage('');
        setQueryParams((prev) => {
          if (prev && typeof prev === 'object') {
            return { ...prev, _lat: fallback.lat, _lon: fallback.lon };
          }
          // No previous params; return minimal, structurally compatible object
          const next: JobSearchQueryParams = { _lat: fallback.lat, _lon: fallback.lon };
          return next;
        });
      }
    }
  }, [queryParams?.location, queryParams?._lat, queryParams?._lon, setQueryParams, setCurrentPage]);

  // Force revalidation when coords become available (and no manual location)
  const revalidatedOnceRef = useRef<string | null>(null); // keep last key to avoid excessive pokes
  useEffect(() => {
    const locArr = queryParams?.location ?? undefined;
    const hasManual = Array.isArray(locArr) && locArr.length > 0;
    const lat = queryParams?._lat;
    const lon = queryParams?._lon;
    const hasCoords = Boolean(lat && lon);

    if (hasCoords && !hasManual) {
      const key = `${lat},${lon}`;
      if (revalidatedOnceRef.current !== key) {
        setCurrentPage('');
        // Poke SWR Infinite to revalidate the first page with the new key
        try { void setSize(1); } catch { /* noop */ }
        revalidatedOnceRef.current = key;
      }
    }
  }, [queryParams?._lat, queryParams?._lon, queryParams?.location, setCurrentPage, setSize]);

  // Remember last “Use my location” coords as well
  const rememberLastCoords = (latitude: number | string, longitude: number | string) => {
    try {
      localStorage.setItem(
        'joblist.lastCoords',
        JSON.stringify({ lat: String(latitude), lon: String(longitude), ts: Date.now() }),
      );
    } catch {
      // ignore storage errors
    }
  };

  return { rememberLastCoords };
};

export default useJobListLocationRestore;
